/**
 * Carries the table chosen in the picker sheet up
 * into the checkout's table context row, and into the
 * sheet's own confirm button, without a round trip.
 *
 * Choosing a table is NOT this controller's job:
 * that is a plain radio group styled with
 * `group-has-[:checked]:`, the house pattern. What
 * CSS cannot do is move the chosen table's *name* out
 * of the sheet and into the row:
 *
 *   - the row is not a following sibling of any
 *     radio, so `peer-checked:` cannot reach it;
 *   - a per-table `peer-checked/<id>:` variant would
 *     have to be built from a database id, and
 *     Tailwind's JIT only emits classes it can read
 *     in the source, so those never compile.
 *
 * The row is not merely a different branch of the
 * tree either: the sheet is rendered OUTSIDE the
 * checkout form (a form inside a form is invalid
 * HTML), so no element wraps both. That is why the
 * two updated nodes are found by a document query on
 * a data attribute rather than by a Stimulus target:
 * targets are scoped to the controller element, and
 * here there is no common ancestor to mount on.
 *
 * The attributes are set by the on-site view, which
 * owns both ends:
 *
 *   [data-table-picker-title]   the row's title
 *   [data-table-picker-action]  the row's "Change"
 *
 * Usage:
 * ```html
 * <div data-controller="table-picker"
 *      data-table-picker-confirm-label-value="Use %{name}"
 *      data-table-picker-action-label-value="Change">
 *   <input type="radio" name="…[table_id]" value="7"
 *          form="checkout_form"
 *          data-table-name="Table 12"
 *          data-action="table-picker#select">
 *   <div data-table-picker-target="confirm"
 *        class="hidden md:flex">
 *     <button type="button">
 *       <span data-table-picker-target="confirmLabel">
 *         Use Table 12
 *       </span>
 *     </button>
 *   </div>
 * </div>
 * ```
 */
import { Controller } from "@hotwired/stimulus";

const TITLE_SELECTOR = "[data-table-picker-title]";
const ACTION_SELECTOR = "[data-table-picker-action]";

export default class extends Controller {
  static targets = ["confirm", "confirmLabel"];
  static values = {
    confirmLabel: { type: String, default: "" },
    actionLabel: { type: String, default: "" }
  };

  /**
   * The row says the table the sheet holds, whoever
   * drew it. A pick is client-side, so a render the
   * server drew without it (applying a discount code
   * redirects back to this page, CAL-522) keeps the
   * customer's radio, through the customer morph, and
   * puts the row back to "Choose your table". Restating
   * from the checked radio on every connect is what
   * lines them up again; where the server drew the
   * table itself it writes the same words.
   */
  connect() {
    const picked = this.element.querySelector(
      'input[type="radio"]:checked'
    );
    if (picked) this.describe(picked.dataset.tableName);
  }

  select(event) {
    const name = event.target.dataset.tableName;
    if (!name) return;

    this.describe(name);

    if (!this.confirmVisible()) this.closeSheet();
  }

  describe(name) {
    if (!name) return;

    this.write(TITLE_SELECTOR, name);

    // "Choose" becomes "Change" once there is
    // something to change.
    if (this.actionLabelValue) {
      this.write(ACTION_SELECTOR, this.actionLabelValue);
    }

    if (this.hasConfirmLabelTarget) {
      this.confirmLabelTarget.textContent =
        this.confirmLabelValue.replace("%{name}", name);
    }
  }

  write(selector, text) {
    const node = document.querySelector(selector);
    if (node) node.textContent = text;
  }

  // `offsetParent` is null for an element hidden by
  // `display: none`, i.e. the footer is behind a
  // `hidden md:flex`, so we are on the bottom sheet
  // and there is nothing left to confirm with.
  //
  // Read off the footer, never off its label: the
  // label is always displayed, but on a phone its
  // container is not.
  confirmVisible() {
    return (
      this.hasConfirmTarget &&
      this.confirmTarget.offsetParent !== null
    );
  }

  closeSheet() {
    window.dispatchEvent(new CustomEvent("closeModal"));

    // Safety net: the window event is the house
    // pattern, but it only lands if the sheet wired
    // `closeModal@window->modal#close`.
    const dialog = this.element.closest("dialog");
    if (dialog && dialog.open) dialog.close();
  }
}
